<script setup lang="ts">
import type { Translate } from '@/plugins/i18n/types';
import { computed, inject, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useToast } from 'vue-toastification';
import Tip from '@/components/tip/Tip.vue';
import { useCoverStore } from '@/features/cover/coverStore';
import { useCoverMutation } from '@/api/mutations/user/useCoverMutation';
import useMeQuery from '@/api/queries/me/useMeQuery';
import { useUserQuery } from '@/api/queries/user/useUserQuery';

import { afCovers } from '@/config/constants';
import { timeout } from '@/helpers/timeout';
import { MixpanelInstance } from '@/plugins/mixpanel/mixpanel';
import router from '@/router';

import BannerTipImg from '@/assets/img/profile/banner-tip.png';

const t = inject('t') as Translate;

const { auth, refetch: refetchAuth } = useMeQuery();
const { refetch: refetchUser } = useUserQuery();

const currentCover = computed(() => auth.value?.branding?.cover);

const { coverIsLoading } = storeToRefs(useCoverStore());

const isDefaultCover = computed(() => {
  const userCover = auth.value?.branding?.cover || '';
  return afCovers.includes(userCover);
});

const selectedAFCover = ref<string>();
const fileToUpload = ref<File>();
const fileInput = ref<HTMLInputElement | null>(null);

const triggerFileUpload = () => {
  if (!fileInput.value) {
    return;
  }
  fileInput.value.click();
};

const coverToUpdate = computed(() => {
  return fileToUpload.value ?? selectedAFCover.value;
});

const { updateCover } = useCoverMutation(coverToUpdate);

async function handleFileUpload(e: InputEvent) {
  const { files } = e.target as HTMLInputElement;

  if (files && files[0]) {
    selectedAFCover.value = undefined;
    fileToUpload.value = files[0];
  }

  await updatePicture();
}

async function updatePicture() {
  if (coverIsLoading.value || coverToUpdate.value === auth.value?.branding?.cover) return false;

  coverIsLoading.value = true;
  try {
    await updateCover();

    await Promise.all([refetchAuth(), refetchUser()]);

    // timeout is used to give enough time for AWS S3 to process the uploading.
    if (coverToUpdate.value && typeof coverToUpdate.value !== 'string') await timeout(4000);

    /** REPORT **/
    if (typeof coverToUpdate.value === 'string') {
      MixpanelInstance.track('profile_cover_update', {
        origin: router.currentRoute.value.name,
      });
    } else if (typeof coverToUpdate.value === 'undefined') {
      MixpanelInstance.track('profile_cover_delete', {
        origin: router.currentRoute.value.matched[0].name,
      });
    } else {
      MixpanelInstance.track('profile_cover_update', {
        origin: router.currentRoute.value.matched[0].name,
      });
    }
  } catch {
    useToast().error(t('general.error.general'));
  }

  coverIsLoading.value = false;
}
</script>

<template>
  <!-- SHOW CURRENT COVER -->
  <div class="flex flex-col items-center p-6">
    <h2 class="text-h2 my-4">{{ $t('profile.photo.banner.title') }}</h2>
    <p class="text-body-2 text-center">
      {{ $t('profile.photo.banner.body') }}
    </p>

    <label
      for="cover-file"
      class="relative h-48 w-full bg-af-grey-200 border border-dashed my-6 cursor-pointer"
      :aria-label="$t('general.upload')"
      :title="$t('general.upload')"
    >
      <div class="flex items-center justify-center h-full text-afgrey-600">
        <div v-if="isDefaultCover" class="text-center">
          <v-icon icon="fas fa-image" color="af-grey-600" :size="40" />
          <p class="text-body-4 mt-2">{{ $t('general.upload') }}</p>
        </div>
        <v-img
          v-else
          cover
          :aspect-ratio="16 / 4"
          :src="!coverIsLoading ? $assets(currentCover) : $assets(afCovers[0])"
        />
      </div>

      <!-- A11Y UPLOAD -->
      <button
        role="button"
        class="inline-flex items-center justify-center gap-2 text-sm font-medium text-nowrap transition-colors outline-hidden duration-300 disabled:pointer-events-none focus-visible:ring-2 focus-visible:ring-afblue-600 focus-visible:ring-offset-2 border border-afgrey-500 text-afgrey-700 hover:bg-afgrey-300 hover:border-afgrey-300 disabled:bg-white disabled:border-afgrey-500 disabled:text-afgrey-500 active:bg-afgrey-300 rounded-full p-0 min-w-0 absolute top-0 end-0 -translate-x-1/4 translate-y-1/4 rtl:-translate-x-1/4 z-10 h-14 w-14 bg-white cursor-pointer focus-within:ring-2 focus-within:ring-afblue-600"
        :disabled="coverIsLoading"
        @click="triggerFileUpload"
      >
        <v-icon v-if="coverIsLoading" icon="fas fa-spinner-third" class="animate-spin" :size="19" />
        <v-icon v-else icon="fas fa-down-to-line" color="iconSolid" :size="19" />
      </button>
    </label>

    <!-- HIDDEN INPUT -->
    <input
      ref="fileInput"
      type="file"
      name="file"
      id="cover-file"
      accept=".jpg, .jpeg, .png"
      v-on:change="handleFileUpload"
    />
    <Tip :imgUrl="BannerTipImg" :width="189" :height="55" :text="$t('profile.photo.banner.tip')" />
  </div>
</template>

<style scoped lang="scss">
#cover-file {
  display: none;
}
.cover {
  &-selection {
    box-shadow: inset 0px 0px 10px 0px #2b2b2b63;
    &_items {
      max-height: 200px;
      overflow-y: scroll;
      &::-webkit-scrollbar {
        display: none;
      }
    }
    &_item {
      position: relative;
      margin: 5px 0;
      width: 100%;
      min-width: 200px;
      transition: all 0.2s ease-in-out;
      @media (min-width: 600px) {
        margin: 3px;
        width: calc(50% - 6px);
      }
      &-img {
        pointer-events: none;
        user-select: none;
      }
      &-loader {
        position: absolute;
        top: calc(50% - 15px);
        left: calc(50% - 15px);
      }
      &-check {
        display: none;
        position: absolute;
        top: calc(50% - 15px);
        left: calc(50% - 15px);
      }
      &:not(.selected, .loading) {
        cursor: pointer;
        &:hover {
          opacity: 0.8;
        }
        &:active {
          transform: scale(0.97);
        }
      }
      &.selected {
        .cover-selection_item {
          &-img {
            opacity: 0.4;
          }
          &-check {
            display: block;
          }
        }
      }
    }
  }
}
</style>
